import { createPortal } from 'react-dom';
import { ClipboardList, X } from 'lucide-react';
import type { HorarioAula, Turma } from '../../../types/models';

type Props = {
  aula: HorarioAula;
  turmaNome?: string;
  qtdAlunos: number;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Confirmação leve antes de iniciar a chamada (desktop + mobile).
 * Portal em document.body: evita `position:fixed` preso a pais com transform
 * (layout / phone simulator) e fica acima da MobileTabBar.
 */
export function ChamadaConfirmStart({
  aula,
  turmaNome,
  qtdAlunos,
  busy,
  onCancel,
  onConfirm,
}: Props) {
  const materia = aula.materia?.nome || `${aula.numeroAula}ª Aula`;
  const turma = turmaNome || aula.turma?.nome || 'Turma';

  const dialog = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="chamada-confirm-title"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100dvh',
        maxHeight: '100vh',
        zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        boxSizing: 'border-box',
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 400,
          background: 'var(--bg-card, #fff)',
          borderRadius: 16,
          border: '1px solid var(--border-color, #e5e7eb)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
          padding: '18px 18px 16px',
          margin: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: 'rgba(37, 99, 235, 0.1)',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ClipboardList size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              id="chamada-confirm-title"
              style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}
            >
              Iniciar chamada?
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: '#64748b', lineHeight: 1.45 }}>
              <strong style={{ color: '#0f172a' }}>{materia}</strong>
              {' · '}
              {turma}
              <br />
              {aula.horaInicio}–{aula.horaFim}
              {' · '}
              {qtdAlunos} aluno{qtdAlunos !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            aria-label="Fechar"
            style={{
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: 1,
              padding: '11px 12px',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              fontWeight: 700,
              fontSize: '0.82rem',
              color: '#475569',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1,
              padding: '11px 12px',
              borderRadius: 10,
              border: 'none',
              background: busy
                ? '#93c5fd'
                : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
              fontWeight: 700,
              fontSize: '0.82rem',
              color: '#fff',
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 4px 12px rgba(37,99,235,0.25)',
            }}
          >
            {busy ? 'Preparando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}

export type { Turma };
